(() => {
  'use strict';

  const ELEMENT_NAME = 'category-row';
  const DRAG_THRESHOLD_PX = 6;
  const EDGE_TOLERANCE_PX = 2;

  if (customElements.get(ELEMENT_NAME)) return;

  class CategoryRow extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;

      this.list = this.querySelector('[data-category-list]');
      if (!this.list) return;

      this.controller = new AbortController();
      this.nav = this.querySelector('[data-category-nav]');
      this.prevButton = this.querySelector('[data-category-prev]');
      this.nextButton = this.querySelector('[data-category-next]');
      this.drag = null;
      this.suppressClick = false;

      this.bindEvents();
      this.resizeObserver = new ResizeObserver(() => this.updateNav());
      this.resizeObserver.observe(this.list);
      this.updateNav();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
    }

    on(target, event, handler, options = {}) {
      target?.addEventListener(event, handler, { ...options, signal: this.controller.signal });
    }

    bindEvents() {
      this.on(this.prevButton, 'click', () => this.scrollByPage(-1));
      this.on(this.nextButton, 'click', () => this.scrollByPage(1));
      this.on(this.list, 'scroll', () => this.updateNav(), { passive: true });

      this.on(this.list, 'pointerdown', (event) => this.onPointerDown(event));
      this.on(this.list, 'pointermove', (event) => this.onPointerMove(event));
      this.on(this.list, 'pointerup', (event) => this.endDrag(event));
      this.on(this.list, 'pointercancel', (event) => this.endDrag(event));
      this.on(this.list, 'dragstart', (event) => event.preventDefault());
      // Capture phase so a drag that ends on a card doesn't also follow its link.
      this.on(this.list, 'click', (event) => {
        if (!this.suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
        this.suppressClick = false;
      }, { capture: true });
    }

    get maxScroll() {
      return this.list.scrollWidth - this.list.clientWidth;
    }

    get isRtl() {
      return getComputedStyle(this.list).direction === 'rtl';
    }

    scrollByPage(direction) {
      const firstItem = this.list.firstElementChild;
      const gap = parseFloat(getComputedStyle(this.list).columnGap) || 0;
      const itemWidth = firstItem ? firstItem.getBoundingClientRect().width + gap : this.list.clientWidth;
      const visibleItems = Math.max(1, Math.floor((this.list.clientWidth + gap) / itemWidth));
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const sign = this.isRtl ? -1 : 1;
      this.list.scrollBy({ left: direction * sign * visibleItems * itemWidth, behavior: reducedMotion ? 'auto' : 'smooth' });
    }

    updateNav() {
      if (!this.nav) return;
      const overflowing = this.maxScroll > EDGE_TOLERANCE_PX;
      this.nav.hidden = !overflowing;
      this.toggleAttribute('data-overflowing', overflowing);
      if (!overflowing) return;

      const position = Math.abs(this.list.scrollLeft);
      if (this.prevButton) this.prevButton.disabled = position <= EDGE_TOLERANCE_PX;
      if (this.nextButton) this.nextButton.disabled = position >= this.maxScroll - EDGE_TOLERANCE_PX;
    }

    onPointerDown(event) {
      // Touch and pen already scroll natively; only mouse needs drag-to-scroll.
      if (event.pointerType !== 'mouse' || event.button !== 0 || this.maxScroll <= EDGE_TOLERANCE_PX) return;
      this.drag = { startX: event.clientX, startScroll: this.list.scrollLeft, pointerId: event.pointerId, moved: false };
    }

    onPointerMove(event) {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;
      const delta = event.clientX - this.drag.startX;
      if (!this.drag.moved) {
        if (Math.abs(delta) < DRAG_THRESHOLD_PX) return;
        this.drag.moved = true;
        this.list.setPointerCapture(event.pointerId);
        this.setAttribute('data-dragging', '');
      }
      this.list.scrollLeft = this.drag.startScroll - delta;
    }

    endDrag(event) {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;
      if (this.drag.moved) {
        this.suppressClick = event.type === 'pointerup';
        window.setTimeout(() => {
          this.suppressClick = false;
        });
        if (this.list.hasPointerCapture(event.pointerId)) this.list.releasePointerCapture(event.pointerId);
        this.removeAttribute('data-dragging');
      }
      this.drag = null;
    }
  }

  customElements.define(ELEMENT_NAME, CategoryRow);
})();
